/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useMemo } from 'react';
import { useDistrictContext } from './context'; // Renamed context hook
import { ACTIONS } from './reducer';
import { apiCaller, APIError, createSearchCriteria, createPageable } from 'utils/axios';
import { API_ENDPOINTS } from 'hooks/api/apiEndpoints';
import Swal from 'sweetalert2';

export const useDistrict = () => {
    const [state, dispatch] = useDistrictContext(); // Updated context hook name

    const setLoading = useCallback(
        (loading) => {
            dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
        },
        [dispatch]
    );

    const showSuccessMessage = useCallback((message) => {
        Swal.fire({
            title: 'Success',
            text: message,
            icon: 'success'
        });
    }, []);

    const handleError = useCallback(
        (error) => {
            let errorMessage = 'An unexpected error occurred';
            let errors = null;

            if (error instanceof APIError) {
                errorMessage = error.message;
                errors = error.errors;
            }

            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error'
            });

            dispatch({ type: ACTIONS.SET_ERROR, payload: { message: errorMessage, errors } });
        },
        [dispatch]
    );

    const setFilter = useCallback(
        (filterData) => {
            dispatch({ type: ACTIONS.SET_FILTER, payload: filterData });
        },
        [dispatch]
    );

    const setSort = useCallback(
        (sortData) => {
            dispatch({ type: ACTIONS.SET_SORT, payload: sortData });
        },
        [dispatch]
    );

    const setPage = useCallback(
        (pageData) => {
            dispatch({ type: ACTIONS.SET_PAGE, payload: pageData });
        },
        [dispatch]
    );

    const fetchDistricts = useCallback(async () => {
        try {
            setLoading(true);
            const { filter, pagination, sort } = state;

            const searchCriteria = Object.entries(filter)
                .filter(([_, value]) => value !== null && value !== undefined)
                .map(([key, value]) =>
                    createSearchCriteria({
                        key,
                        value,
                        operation: 'EQUAL'
                    })
                );

            const pageable = createPageable({
                page: pagination.page,
                size: pagination.size,
                sort: [{ field: sort.field, direction: sort.direction }]
            });

            const data = await apiCaller.search(API_ENDPOINTS.DISTRICTS, searchCriteria, pageable);
            dispatch({ type: ACTIONS.SET_DISTRICTS, payload: data });
            return data;
        } catch (error) {
            handleError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    }, [setLoading, handleError, dispatch, state]);

    const fetchDistrictsByProvince = useCallback(
        async (provinceId) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.DISTRICTS}/province/${provinceId}`);

                dispatch({ type: ACTIONS.SET_ALL_DISTRICTS, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch, state]
    );

    const searchDistricts = useCallback(
        async (searchParams = {}, pageParams = {}) => {
            setFilter(searchParams);
            setPage(pageParams);
            return fetchDistricts();
        },
        [setFilter, setPage, fetchDistricts]
    );

    const createDistrict = useCallback(
        async (districtData) => {
            try {
                setLoading(true);
                const data = await apiCaller.post(API_ENDPOINTS.DISTRICTS, districtData);
                dispatch({ type: ACTIONS.ADD_DISTRICT, payload: data });

                showSuccessMessage('District created successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const updateDistrict = useCallback(
        async (id, districtData) => {
            try {
                setLoading(true);
                const data = await apiCaller.put(`${API_ENDPOINTS.DISTRICTS}/${id}`, districtData);
                dispatch({ type: ACTIONS.UPDATE_DISTRICT, payload: { id, data } });

                showSuccessMessage('District updated successfully');
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const deleteDistrict = useCallback(
        async (id) => {
            try {
                setLoading(true);
                await apiCaller.delete(`${API_ENDPOINTS.DISTRICTS}/${id}`);
                dispatch({ type: ACTIONS.DELETE_DISTRICT, payload: id });

                showSuccessMessage('District deleted successfully');
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, showSuccessMessage, handleError, dispatch]
    );

    const getDistrictById = useCallback(
        async (id) => {
            try {
                setLoading(true);
                const data = await apiCaller.get(`${API_ENDPOINTS.DISTRICTS}/${id}`);
                dispatch({ type: ACTIONS.SET_SELECTED_DISTRICT, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    const clearError = useCallback(() => {
        dispatch({ type: ACTIONS.CLEAR_ERROR });
    }, [dispatch]);

    const listDistrict = useCallback(
        async (page = 0, size = 10) => {
            try {
                setLoading(true);
                const data = await apiCaller.list(API_ENDPOINTS.DISTRICTS, page, size);
                dispatch({ type: ACTIONS.SET_DISTRICTS, payload: data });
                return data;
            } catch (error) {
                handleError(error);
                throw error;
            } finally {
                setLoading(false);
            }
        },
        [setLoading, handleError, dispatch]
    );

    return useMemo(
        () => ({
            // State
            districts: state.districts, // Renamed to districts
            selectedDistrict: state.selectedDistrict, // Renamed to selectedDistrict
            loading: state.loading,
            error: state.error,
            filter: state.filter,
            pagination: state.pagination,
            sort: state.sort,
            lastUpdated: state.lastUpdated,
            provinceId: state.provinceId,
            listDistrict,

            // List Operations
            setFilter,
            setSort,
            setPage,
            fetchDistricts,
            searchDistricts,
            fetchDistrictsByProvince,

            // CRUD Operations
            createDistrict,
            updateDistrict,
            deleteDistrict,
            getDistrictById,

            // Utilities
            clearError
        }),
        [
            state,
            setFilter,
            setSort,
            setPage,
            fetchDistricts,
            searchDistricts,
            createDistrict,
            updateDistrict,
            deleteDistrict,
            getDistrictById,
            clearError
        ]
    );
};

export default useDistrict;
